import { ChatMessageFragmentType } from "@/models/ai-chat/ChatMessageFragment";
import { ChatMessageStream, ChatMessageStreamEventType } from "@/models/ai-chat/ChatMessageStream";
import { Dispatch, MutableRefObject, SetStateAction, useEffect } from "react";

/* 
  Attempts to get the value of a property from an incomplete JSON.
  If the property we are looking for was not yet completed, we return it or not based on returnIncompleteProperty

  { "type": "ST -> "ST"
  { "type": "STRING", -> "STRING"
  { "type": "STRING", "otherProperty -> "STRING"
*/
export const tryToParseTextPropertyFromIncompleteJson = (
  propertyName: string,
  serializedJSON: string,
  returnIncompleteProperty: boolean
): string | null => {
  const regex = new RegExp(`"${propertyName}"\\s*:\\s*"([^"]*)`);
  const match = serializedJSON.match(regex);

  if (match && match[1]) {
    if (!returnIncompleteProperty && serializedJSON[serializedJSON.indexOf(match[1]) + match[1].length] !== '"') {
      return null;
    }
    return match[1];
  }

  return null;
};

type StreamedMessageHookProps = {
  stream: ChatMessageStream;
  setText: Dispatch<SetStateAction<string>>;
  onStart: () => void;
  onFinish: () => void;
  raw: MutableRefObject<string>;
};

export const useStreamedMessage = (props: StreamedMessageHookProps) => {
  useEffect(() => {
    props.setText(props.stream.content || "");
    props.raw.current = "";

    const handleReceiveChunk = (chunk: string) => {
      props.raw.current += chunk;

      // We should only display Strings while they are being streamed. Every other type must wait until its completed.
      const shouldDisplayWhileStreaming =
        tryToParseTextPropertyFromIncompleteJson("type", props.raw.current, false) === ChatMessageFragmentType.STRING;

      const isJSON = props.raw.current.length != 0 && props.raw.current.trim()[0] == "{";

      props.onStart();
      if (shouldDisplayWhileStreaming) {
        props.setText(tryToParseTextPropertyFromIncompleteJson("content", props.raw.current, true) || "");
      } else if (!isJSON) {
        props.setText(props.raw.current);
      }
    };

    const handleStreamCompletition = () => {
      props.onFinish();
    };

    props.stream.addEventListener(ChatMessageStreamEventType.CONTENT_RECEIVED, handleReceiveChunk);
    props.stream.addEventListener(ChatMessageStreamEventType.COMPLETED, handleStreamCompletition);

    return () => {
      props.stream.removeEventListener(ChatMessageStreamEventType.CONTENT_RECEIVED, handleReceiveChunk);
      props.stream.removeEventListener(ChatMessageStreamEventType.COMPLETED, handleStreamCompletition);
    };
  }, [props.stream]);
};
